//import { useWhyDidYouUpdate } from 'hooks';
import { FieldAttributes, useField, useFormikContext } from 'formik';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Form, OverlayTrigger } from 'react-bootstrap';

import { OptionType } from '../select-db';

import { FieldLabel, SelectDB } from './../index';
import FieldTooltip from './field-tooltip';

type Props = {
  readonly as?: React.ElementType<any>;
  readonly hook: () => any;
  readonly where?: string;
  readonly name: string;
  readonly label?: string;
  readonly recordLabel?: string;
  readonly recordLabel2?: string;
  readonly recordLabel3?: string;
  readonly formatLabel?: (record: any) => string;
  readonly sbLabel: string;
  readonly placeholder?: string;
  readonly disableInactives?: boolean;
  readonly disabled?: boolean;
  readonly required?: boolean;
  readonly minMenuHeight?: number;
  readonly maxMenuHeight?: number;
  readonly onChange?: (optioms: OptionType) => void;
  readonly children?: any;
  readonly isMulti?: boolean;
  readonly height?: number;
  readonly noLabel?: number;
  readonly sortOptionValues?: boolean;
  readonly multiDisplayMode?: 'chips' | 'summary';
  readonly formatMultiSummary?: (selected: readonly OptionType[]) => string;
  readonly formatTooltipValue?: (value: unknown) => string;
  readonly showOptionCheckbox?: boolean;
} & FieldAttributes<any>;

export const FieldSelectDB = ({
  as = 'div',
  hook,
  name,
  where = '',
  sbLabel = '',
  label = 'Label',
  recordLabel = 'description',
  recordLabel2 = 'description2',
  recordLabel3 = 'description3',
  formatLabel = null,
  placeholder = '',
  disableInactives = false,
  disabled = false,
  required = false,
  minMenuHeight,
  maxMenuHeight,
  onChange = null,
  children = null,
  isMulti = false,
  height = 22,
  noLabel = false,
  sortOptionValues = true,
  multiDisplayMode = 'chips',
  formatMultiSummary = undefined,
  formatTooltipValue = undefined,
  showOptionCheckbox = false,
}: Props) => {
  //
  //-------------------- Body -----------------------------------------------
  //
  const [{ value }, { touched, error, initialValue }] = useField<number>(name);
  const { setFieldValue, setFieldTouched, submitCount } = useFormikContext<any>();

  const latestOnChange = useRef(onChange);
  useEffect(() => {
    // eslint-disable-off-next-line-off functional/immutable-data
    latestOnChange.current = onChange;
  }, [onChange]);

  const setFieldValueAndTouched = useCallback(
    async (_value, touched: boolean) => {
      // Handle multi-select properly
      if (isMulti) {
        await setFieldValue(
          name,
          _value.map((item) => item.value),
          true,
        ); // Store an array of selected values
      } else {
        await setFieldValue(name, _value, true);
      }
      await setFieldTouched(name, touched);
    },
    [name, setFieldTouched, setFieldValue, isMulti],
  );

  // useEffect(() => {
  //   console.log('[FieldSelectDB] where changed');
  //   if (value !== null && where !== '') setFieldValueAndTouched(null, false);
  //   // eslint-disable-off-next-line-off react-hooks/exhaustive-deps
  // }, [setFieldValueAndTouched, where]);

  //-------------------- Handlers -------------------------------------------
  //
  const handleChange = useCallback(
    (_option: OptionType | readonly OptionType[]) => {
      console.log('$$$ ## [field-select-db] handleChange', isMulti, _option);

      if (isMulti) {
        setFieldValueAndTouched(_option, true); // ✅ Pass array of selected values
      } else {
        if (!Array.isArray(_option)) {
          setFieldValueAndTouched((_option as OptionType).value, true); // ✅ Pass single selected value
        }
      }

      !!latestOnChange.current && latestOnChange.current(_option);
    },
    [setFieldValueAndTouched, isMulti],
  );

  const handleBlur = useCallback(async () => {
    await setFieldTouched(name, true);
  }, [setFieldTouched, name]);

  const changed = value !== initialValue;
  const tooltipValue =
    formatTooltipValue?.(value) ??
    (value == null ? '' : isMulti && Array.isArray(value) ? JSON.stringify(value) : String(value));

  // useWhyDidYouUpdate(`[FieldSelectDB:${name}]`, {
  //   as,
  //   hook,
  //   where,
  //   label,
  //   sbLabel,
  //   placeholder,
  //   disabled,
  //   required,
  //   minMenuHeight,
  //   maxMenuHeight,
  //   value,
  //   error,
  // });
  //
  //-------------------- Render -------------------------------------------
  //
  return useMemo(
    () => (
      <Form.Group as={as} controlId={name} className="mb-2">
        {!noLabel && <FieldLabel text={label} changed={changed} required={required} />}
        <OverlayTrigger
          delay={{ show: 400, hide: 400 }}
          overlay={
            <FieldTooltip
              text={name}
              value={tooltipValue}
              alwaysShow={isMulti}
              valueOnly={isMulti}
            />
          }
          placement="top"
        >
        <div>
          <SelectDB
            id={name}
            className={name}
            hook={hook}
            disabled={disabled}
            required={required}
            where={where}
            placeholder={placeholder}
            value={value}
            label={sbLabel}
            recordLabel={recordLabel}
            recordLabel2={recordLabel2}
            recordLabel3={recordLabel3}
            formatLabel={formatLabel}
            maxMenuHeight={maxMenuHeight}
            minMenuHeight={minMenuHeight}
            onChange={handleChange}
            onBlur={handleBlur}
            isInvalid={(touched || submitCount > 0) && !!error}
            disableInactives={disableInactives}
            isMulti={isMulti}
            height={height}
            sortOptionValues={sortOptionValues}
            multiDisplayMode={multiDisplayMode}
            formatMultiSummary={formatMultiSummary}
            showOptionCheckbox={showOptionCheckbox}
          />
          {(touched || submitCount > 0) && !!error && (
            <Form.Control.Feedback type="invalid" className="d-block">
              {error}
            </Form.Control.Feedback>
          )}
          {children}
        </div>
        </OverlayTrigger>
      </Form.Group>
    ),
    [
      as,
      name,
      label,
      changed,
      required,
      value,
      hook,
      disabled,
      where,
      placeholder,
      sbLabel,
      recordLabel,
      recordLabel2,
      maxMenuHeight,
      minMenuHeight,
      handleChange,
      handleBlur,
      touched,
      submitCount,
      error,
      disableInactives,
      isMulti,
      multiDisplayMode,
      formatMultiSummary,
      formatTooltipValue,
      showOptionCheckbox,
      tooltipValue,
      height,
    ],
  );
};
